<template>
  <div class="sidebar-main-menu my-1">
    <template v-for="(menuItem, index) in menu">
      <!-- Top-level leaf item -->
      <div
        v-if="!menuItem.children"
        :key="`root-leaf-${index}`"
        class="sidebar-menu-item my-1"
        :class="{ 'mb-3': index === menu.length - 1 }"
      >
        <eg-button
          v-b-tooltip.hover.right="isMenuHided ? menuItem.name : ''"
          type="ordinary"
          :to="menuItem.href ? null : menuItem.to"
          :href="menuItem.href"
          :target="menuItem.target"
          :icon="menuItem.icon"
          :class="menuItem.class"
          icon-size="medium"
          class="menu-item-button"
          :active="isItemActive(menuItem)"
          @click="menuItem.onClick && menuItem.onClick()"
        >
          <transition name="sidebar-maximized-transition">
            <span v-if="!isMenuHided" class="menu-item-label">
              <span>{{ menuItem.name }}</span>
              <span v-if="menuItem.badge" class="menu-item-badge">{{
                menuItem.badge
              }}</span>
            </span>
          </transition>
        </eg-button>
      </div>

      <!-- Top-level collapsible item — children rendered recursively -->
      <eg-collapsible-item
        v-else
        :key="`root-group-${index}`"
        :ref="menuItem.ref || `group-${index}`"
        :label="menuItem.name"
        :icon="menuItem.icon"
        :button-class="menuItem.class"
        :is-menu-hided="isMenuHided"
        :initial-expanded="isExpanded(menuItem)"
        :tooltip="isMenuHided ? menuItem.name : ''"
        @toggle="handleGroupToggle(index, $event)"
      >
        <sidebar-menu-items
          :ref="menuItem.ref ? menuItem.ref + 'Items' : null"
          :items="menuItem.children"
          :is-menu-hided="isMenuHided"
        />
      </eg-collapsible-item>
    </template>
  </div>
</template>

<script>
import EgCollapsibleItem from "@/components/global/sidebar/eg_collapsible_item.vue";
import SidebarMenuItems from "@/components/global/sidebar/sidebar_menu_items.vue";
import { onboardingActions } from "@/init/onboarding/onboardingActions";
import sidebarAccordionMixin from "@/mixins/sidebarAccordionMixin";

export default {
  name: "SidebarMainMenu",

  components: {
    EgCollapsibleItem,
    SidebarMenuItems,
  },

  mixins: [sidebarAccordionMixin],

  props: {
    menu: {
      type: Array,
      required: true,
      default: () => [],
    },
    isMenuHided: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    currentRoute() {
      return this.$route.path;
    },

    accordionItems() {
      return this.menu;
    },
  },

  mounted() {
    onboardingActions.openHelpMenu = this.expandHelpMenu;
    onboardingActions.closeHelpMenu = this.collapseHelpMenu;
  },

  beforeDestroy() {
    onboardingActions.openHelpMenu = () => {};
    onboardingActions.closeHelpMenu = () => {};
  },

  methods: {
    isExpanded(item) {
      if (!item.children) return false;

      return item.children.some(
        (child) =>
          this.currentRoute === child.to ||
          (child.activeMatcher && child.activeMatcher(this.currentRoute)) ||
          (child.children && this.isExpanded(child))
      );
    },

    isItemActive(menuItem) {
      if (!menuItem.activeMatcher) return false;
      return menuItem.activeMatcher(this.currentRoute);
    },

    expandHelpMenu() {
      const helpMenuIndex = this.menu.findIndex(
        (item) => item.ref === "helpMenu"
      );
      if (helpMenuIndex !== -1) {
        this.handleGroupToggle(helpMenuIndex, true);
      }
      const helpMenu = this.$refs.helpMenu?.[0];
      if (!helpMenu) {
        return;
      }
      helpMenu.open();
      this.$nextTick(() => {
        this.$refs.helpMenuItems?.[0]?.openItem("lrMenu");
      });
    },

    collapseHelpMenu() {
      this.$refs.helpMenuItems?.[0]?.closeItem("lrMenu");
      const helpMenu = this.$refs.helpMenu?.[0];
      if (helpMenu) {
        helpMenu.close();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sidebar-main-menu {
  padding: 16px 0 16px 0;

  .sidebar-menu-item {
    padding: 0 20px;
    @media (max-height: 800px) {
      margin: 0 !important;
    }
    span {
      font-size: 0.875rem;
      line-height: 1.125rem;
    }

    .menu-item-label {
      display: inline-flex;
      align-items: center;
    }

    .menu-item-badge {
      position: absolute;
      right: 10px;
      margin: 0;
      padding: 1px 6px;
      font-size: 0.625rem;
      font-weight: 600;
      line-height: 1rem;
      border-radius: 100px;
      background-color: var(--color-surface-primary-default);
      color: white;
      vertical-align: middle;
    }
  }

  .sidebar-menu-item:first-child {
    padding-top: 0;
  }

  .menu-item-button {
    /* Override EgButton defaults for sidebar menu */
    padding: 0 12px;
    border-radius: 12px;
    height: 40px;
    width: 100%;
    justify-content: start;
    font-family: Inter, sans-serif;
    /* Default colors and font weight */
    color: var(--color-text-neutral-on-white);
    font-weight: 400;

    ::v-deep .eg-icon,
    ::v-deep .icon .eg-icon {
      color: var(--color-icon-neutral-neutral);
    }

    /* Active state styling */
    &.router-link-exact-active,
    &.eg-new-button--active {
      background-color: var(--color-surface-neutral-press);
      color: var(--color-text-neutral-on-white);

      ::v-deep .eg-icon,
      ::v-deep .icon .eg-icon {
        color: var(--color-icon-primary-primary) !important;
      }
    }

    /* Hover and focus-visible states */
    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background-color: var(--color-surface-neutral-hover);
      color: var(--color-text-neutral-on-white);
    }

    /* Press state */
    &:active {
      background-color: var(--color-surface-neutral-press);
      color: var(--color-text-neutral-on-white);
    }
  }
  @media (max-height: 769px) {
    padding: 0;
  }
}
</style>

<style lang="scss">
.is-sidebar-minimized .sidebar-wrapper {
  .sidebar-main-menu {
    .sidebar-menu-item {
      padding: 0 8px;

      .menu-item-button {
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-width: 40px;

        .button-text {
          display: none !important;
        }
      }
      @media (max-height: 800px) {
        margin: 0 !important;
      }
    }
  }
}
</style>
